import { ApiError } from '@/api/client'
import type { OverLimit, QuotaUsage } from '@/api/types'

/**
 * Pure helpers behind the Plan page, the over-limit banner and the quota upgrade prompt.
 *
 * The prices here are DISPLAY copy only (stripe.md B3, B9): what is charged is decided by the Stripe
 * catalogue server-side. If the two ever disagree, Stripe's Checkout page is the truth the customer
 * pays, so keep this table in step with the catalogue.
 */

export type BillingInterval = 'month' | 'year'

export interface PlanPricing {
  month: number
  year: number
}

/** Dollars. Annual is ten months for twelve (B3). Free has no row: it is never sold. */
export const PLAN_PRICES: Readonly<Record<string, PlanPricing>> = {
  solo: { month: 12, year: 120 },
  team: { month: 29, year: 290 },
}

/** Team only: each ACTIVE member beyond the five included (B9). */
export const SEAT_PRICE: PlanPricing = { month: 5, year: 50 }
export const TEAM_INCLUDED_SEATS = 5

export const SELLABLE_PLANS: readonly string[] = ['solo', 'team']

export function formatUsd(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`
}

/** `$12 / month`, `$120 / year`; null for a plan that is not sold. */
export function priceLabel(planCode: string, interval: BillingInterval): string | null {
  const price = PLAN_PRICES[planCode]
  if (price === undefined) return null
  return `${formatUsd(price[interval])} / ${interval}`
}

export function seatPriceLabel(interval: BillingInterval): string {
  return `+${formatUsd(SEAT_PRICE[interval])} / ${interval} per extra seat`
}

/** What each plan includes, in the words the page shows. Free is stated plainly (§11). */
export function planIncludes(planCode: string): string {
  switch (planCode) {
    case 'free':
      return '1 zone, 3 connections, 30 days of history, free for ever.'
    case 'solo':
      return 'Up to 10 zones, 15 connections and 25 channels per zone, 6 months of history, one member.'
    case 'team':
      return `Up to 50 zones, 50 connections and 100 channels per zone, unlimited history, ${TEAM_INCLUDED_SEATS} members included (${seatPriceLabel('month')}).`
    default:
      return ''
  }
}

/** A plan that outranks the current one is an Upgrade; a lower one is a Change. */
export function planRank(planCode: string): number {
  return ['free', 'solo', 'team'].indexOf(planCode)
}

/* -------------------------------------------------------------------------- */
/* Quota refusals                                                              */
/* -------------------------------------------------------------------------- */

export type QuotaCode = 'zone_limit' | 'connection_limit' | 'channel_limit' | 'member_limit'

const QUOTA_NOUN: Readonly<Record<QuotaCode, string>> = {
  zone_limit: 'zones',
  connection_limit: 'connections per zone',
  channel_limit: 'channels per zone',
  member_limit: 'members',
}

export interface UpgradeHint {
  code: QuotaCode
  limit: number | null
  /** The plan code that lifts the limit, or null when no sellable plan does. */
  upgradePlan: string | null
  noun: string
}

function isQuotaCode(value: string | undefined): value is QuotaCode {
  return value !== undefined && value in QUOTA_NOUN
}

/**
 * The upgrade prompt for a refused quota, or null when the error is anything else. A quota refusal
 * is a 409 of type `limit-exceeded` carrying a stable `code`, the `limit` and the `upgrade_plan`.
 */
export function upgradeHint(error: unknown): UpgradeHint | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const problem = error.problem
  if (problem === undefined || !(problem.type ?? '').endsWith('/limit-exceeded')) return null
  if (!isQuotaCode(problem.code)) return null
  const upgrade = problem.upgrade_plan
  return {
    code: problem.code,
    limit: typeof problem.limit === 'number' ? problem.limit : null,
    upgradePlan: typeof upgrade === 'string' ? upgrade : null,
    noun: QUOTA_NOUN[problem.code],
  }
}

/** One sentence naming the plan that lifts the limit. */
export function upgradeSentence(hint: UpgradeHint, planName: (code: string) => string): string {
  const limit = hint.limit === null ? '' : `${hint.limit} `
  if (hint.upgradePlan === null) {
    return `Your plan allows ${limit}${hint.noun}, and no larger plan is on sale. Contact us if you need more.`
  }
  return `Your plan allows ${limit}${hint.noun}. ${planName(hint.upgradePlan)} lifts that limit.`
}

/* -------------------------------------------------------------------------- */
/* Over-limit grace                                                            */
/* -------------------------------------------------------------------------- */

export interface OverLimitLine {
  label: string
  inUse: number
  limit: number
}

/** The quotas that are actually exceeded, as printable rows. */
export function overLimitLines(over: OverLimit): OverLimitLine[] {
  const rows: [string, QuotaUsage | null | undefined][] = [
    ['Zones', over.zones],
    ['Connections per zone', over.connections_per_zone],
    ['Channels per zone', over.channels_per_zone],
    ['Members', over.members],
  ]
  const lines: OverLimitLine[] = []
  for (const [label, usage] of rows) {
    if (usage !== null && usage !== undefined && usage.over) {
      lines.push({ label, inUse: usage.in_use, limit: usage.limit })
    }
  }
  return lines
}

/** Whole days left until `deadline` (never negative). */
export function daysUntil(deadline: string | null | undefined, now = Date.now()): number | null {
  if (deadline === null || deadline === undefined) return null
  const ms = Date.parse(deadline) - now
  if (Number.isNaN(ms)) return null
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

/**
 * How many zones the admin must keep to satisfy the zone cap: the choice is valid when it names
 * exactly that many (or fewer when fewer exist). The server validates; this only gates the button.
 */
export function keepSelectionValid(selected: readonly string[], zoneLimit: number): boolean {
  return selected.length > 0 && selected.length <= zoneLimit
}

/* -------------------------------------------------------------------------- */
/* Money in                                                                    */
/* -------------------------------------------------------------------------- */

export const MIN_TOPUP_USD = 25

export type AmountCheck = { ok: true; value: number } | { ok: false; reason: string }

/** A dollar amount typed into the top-up or recharge form: at least `minimum`, at most 2 decimals. */
export function checkUsdAmount(raw: string, minimum: number = MIN_TOPUP_USD): AmountCheck {
  const text = raw.trim()
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    return { ok: false, reason: 'Enter a dollar amount with at most two decimals.' }
  }
  const value = Number.parseFloat(text)
  if (value < minimum) return { ok: false, reason: `The minimum is ${formatUsd(minimum)}.` }
  return { ok: true, value }
}

/* -------------------------------------------------------------------------- */
/* Return from Stripe                                                          */
/* -------------------------------------------------------------------------- */

export type StripeReturn =
  | { kind: 'checkout'; outcome: 'success' | 'cancelled'; sessionId: string | null }
  | { kind: 'topup'; outcome: 'success' | 'cancelled'; sessionId: string | null }
  | null

/**
 * Reads `?checkout=` / `?topup=` from a query string. Embedded Checkout returns `complete` (with the
 * `session_id`); `success` and `cancelled` are older links. Unknown values are ignored.
 */
export function stripeReturn(search: string): StripeReturn {
  const params = new URLSearchParams(search)
  const sessionId = params.get('session_id')
  for (const kind of ['checkout', 'topup'] as const) {
    const value = params.get(kind)
    if (value === 'complete' || value === 'success') return { kind, outcome: 'success', sessionId }
    if (value === 'cancelled') return { kind, outcome: 'cancelled', sessionId }
  }
  return null
}

/** Stripe statuses under which the entitlement is held (mirrors `SubscriptionStatus.grantsPlan`). */
export const GRANTING_STATUSES: readonly string[] = ['active', 'trialing', 'past_due']

/**
 * A paid plan is in force: the checkout-return page can stop saying "Activating…". From a
 * complimentary plan (source `staff`, already a paid plan code under `active`) that is not yet the
 * new subscription: wait until the webhook makes the source `stripe` (bug-224).
 */
export function subscriptionActivated(billing: { plan_code: string; status: string; source?: string | null }): boolean {
  if (billing.source !== undefined && billing.source !== 'stripe') return false
  return billing.plan_code !== 'free' && GRANTING_STATUSES.includes(billing.status)
}

/**
 * A Stripe subscription exists that Checkout must not duplicate (the server answers 409
 * `subscription_exists`); changes go through the portal instead.
 */
export function hasLiveSubscription(billing: { source: string | null; status: string }): boolean {
  return (
    billing.source === 'stripe' &&
    ['active', 'trialing', 'past_due', 'unpaid', 'paused', 'incomplete'].includes(billing.status)
  )
}

/** Plain words for a status badge. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Active'
    case 'trialing':
      return 'Trial'
    case 'past_due':
      return 'Payment overdue'
    case 'unpaid':
      return 'Unpaid'
    case 'canceled':
      return 'Canceled'
    case 'incomplete':
      return 'Awaiting first payment'
    case 'incomplete_expired':
      return 'Expired'
    case 'paused':
      return 'Paused'
    default:
      return 'No subscription'
  }
}

/* -------------------------------------------------------------------------- */
/* Staff                                                                       */
/* -------------------------------------------------------------------------- */

const STAFF_LIVE_STATUSES = ['active', 'trialing', 'past_due', 'unpaid', 'paused', 'incomplete']

/**
 * A live Stripe subscription: a staff plan change is refused (409 `subscription_live`) because two
 * sources must never fight over one account. Case-insensitive: the staff view serialises enums.
 */
export function staffSubscriptionLive(
  subscription: { source: string | null; status: string; stripe_subscription_id: string | null } | null | undefined,
): boolean {
  if (subscription === null || subscription === undefined) return false
  return (
    (subscription.source ?? '').toLowerCase() === 'stripe' &&
    subscription.stripe_subscription_id !== null &&
    STAFF_LIVE_STATUSES.includes(subscription.status.toLowerCase())
  )
}

/** What a recharge-failure row means for the operator. UNKNOWN blocks auto-recharge until settled. */
export function rechargeStateNote(state: string): string {
  switch (state.toUpperCase()) {
    case 'UNKNOWN':
      return 'Needs manual settlement in Stripe. Auto-recharge is blocked for this account until it is settled.'
    case 'DECLINED':
      return 'The card was declined and auto-recharge was switched off.'
    case 'FAILED':
      return 'Stripe could not be reached or refused the charge.'
    default:
      return ''
  }
}

/* -------------------------------------------------------------------------- */
/* Taxed top-ups, refunds and settling (2026-10-TOPUP-TAX, Phase 2)            */
/* -------------------------------------------------------------------------- */

/** Whole micros of a validated dollar string (at most two decimals), never through a float product. */
export function usdToMicros(text: string): number {
  const [whole = '0', cents = ''] = text.trim().split('.')
  return (Number(whole) * 100 + Number(cents.padEnd(2, '0'))) * 10_000
}

export type RefundAmountCheck = { ok: true; value: number; micros: number } | { ok: false; reason: string }

/**
 * The refund request form: a positive dollar amount with at most two decimals that does not exceed
 * what is refundable now. The server enforces the same cap (422 `refund_exceeds_refundable`); this
 * only gates the button and says why.
 */
export function checkRefundAmount(raw: string, refundableMicros: number): RefundAmountCheck {
  const text = raw.trim()
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    return { ok: false, reason: 'Enter a dollar amount with at most two decimals.' }
  }
  const micros = usdToMicros(text)
  if (micros <= 0) return { ok: false, reason: 'The amount must be more than zero.' }
  if (micros > refundableMicros) {
    return { ok: false, reason: 'That is more than is refundable now.' }
  }
  return { ok: true, value: Number.parseFloat(text), micros }
}

/** Plain words for a refund request's state, as its admin reads it. */
export function refundRequestLabel(state: string): string {
  switch (state.toUpperCase()) {
    case 'REQUESTED':
      return 'Waiting for review'
    case 'APPROVED':
    case 'PROCESSING':
      return 'Approved, refunding'
    case 'DONE':
      return 'Refunded'
    case 'PENDING_MANUAL':
      return 'Approved, being paid out by our team'
    case 'REJECTED':
      return 'Declined'
    default:
      return state.toLowerCase()
  }
}

/** A request still holds its amount aside (it cannot be spent or requested twice). */
export function refundRequestOpen(state: string): boolean {
  return ['REQUESTED', 'APPROVED', 'PROCESSING', 'PENDING_MANUAL'].includes(state.toUpperCase())
}

/** Only an https document link is rendered as a link; anything else is shown as nothing. */
export function safeDocumentUrl(url: string | null | undefined): string | null {
  if (url === null || url === undefined) return null
  try {
    return new URL(url).protocol === 'https:' ? url : null
  } catch {
    return null
  }
}

/**
 * The customer's document for a ledger row: a top-up's tax invoice or a refund's credit note. It is
 * always one of OUR paths (`/billing/invoices/{id}/pdf`), downloaded with the session; never a Stripe
 * URL. Null when the row has none (a charge, a goodwill credit) or the path is not ours.
 */
export function ledgerDocument(entry: {
  kind: string
  document_path?: string | null
}): { label: string; path: string } | null {
  const path = entry.document_path
  if (path === null || path === undefined || !/^(\/api)?\/billing\/(invoices|credit-notes)\/[^/]+\/pdf$/.test(path)) return null
  switch (entry.kind) {
    case 'TOPUP':
      return { label: 'Invoice', path }
    case 'REFUND':
      return { label: 'Credit note', path }
    default:
      return null
  }
}

export type SettleOutcome = 'charged' | 'not_charged'

/** A Stripe PaymentIntent or invoice id, the only things a settle may name. */
export function isStripeChargeId(value: string): boolean {
  return /^(pi|in)_[A-Za-z0-9]+$/.test(value.trim())
}

/** The settle dialog: "charged" needs the Stripe object id; "not charged" needs none. */
export function settleProblem(outcome: SettleOutcome, stripeId: string): string | null {
  if (outcome === 'not_charged') return null
  if (stripeId.trim() === '') return 'Paste the Stripe PaymentIntent or invoice id that was charged.'
  if (!isStripeChargeId(stripeId)) return 'That is not a PaymentIntent (pi_…) or invoice (in_…) id.'
  return null
}

/** Integer cents as dollars for display (`null` is a dash); the division only happens here. */
export function centsLabel(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? '—' : formatUsd(cents / 100)
}
